import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { Card } from "./Card";
import { ErrorState } from "./states";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches any render-time failure below it and shows the generic card instead of a white
 * screen. React still has no hook for this, so it is a class component — the login app's, for
 * the same reason.
 *
 * The real error is logged for our own diagnostics; the person sees no vendor detail (issue
 * #57 acceptance). What it must never do here is put family language on the screen or in the
 * console: a screen's failure could otherwise print what somebody's family told Boone, so only
 * the error is logged and never the data that was being rendered.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("control panel error", error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Card>
          <ErrorState
            title="Something went wrong"
            body="Reload the page to try again. Nothing you've told Boone is affected."
          />
        </Card>
      );
    }
    return this.props.children;
  }
}
